import { useState, useCallback } from 'react'
import { fetchWithAuth } from '@/lib/fetchWithAuth'
import type {
  AgentResponse,
  AgentDetailResponse,
  AgentRegistrationRequest,
  AgentUpdateRequest,
  AgentStatus,
  CheckBaseUrlResponse,
  DatabaseConnectionInfo,
  DatabaseConnectionConfig,
} from '@/types/api'

const API_BASE = '/api'

interface UseAgentRegistryReturn {
  agents: AgentResponse[]
  loading: boolean
  error: string | null
  listAgents: (status?: AgentStatus) => Promise<AgentResponse[]>
  getAgent: (agentId: string) => Promise<AgentDetailResponse>
  registerAgent: (data: AgentRegistrationRequest) => Promise<AgentResponse>
  updateAgent: (agentId: string, data: AgentUpdateRequest) => Promise<AgentResponse>
  deleteAgent: (agentId: string) => Promise<void>
  validateAgent: (agentId: string) => Promise<Record<string, unknown>>
  healthCheckAgent: (agentId: string) => Promise<boolean>
  activateAgent: (agentId: string) => Promise<AgentResponse>
  deactivateAgent: (agentId: string) => Promise<AgentResponse>
  refreshAgents: () => Promise<void>
  testConnection: (agentId: string) => Promise<{ connected: boolean; status?: string; last_tested_at?: string }>
  getSchema: (agentId: string) => Promise<Record<string, unknown>>
  introspectSchema: (agentId: string) => Promise<Record<string, unknown>>
  enrichSchema: (agentId: string) => Promise<Record<string, unknown>>
  testConnectionAdhoc: (config: DatabaseConnectionConfig) => Promise<{ connected: boolean; message: string }>
  checkBaseUrl: (baseUrl: string, excludeAgentId?: string) => Promise<CheckBaseUrlResponse>
  getDbConnection: (agentId: string) => Promise<DatabaseConnectionInfo>
  fetchAgentInfo: (baseUrl: string) => Promise<{ description?: string } | null>
}

export function useAgentRegistry(): UseAgentRegistryReturn {
  const [agents, setAgents] = useState<AgentResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleError = useCallback((err: unknown): string => {
    if (err instanceof Error) {
      return err.message
    }
    return 'An unexpected error occurred'
  }, [])

  const listAgents = useCallback(async (status?: AgentStatus): Promise<AgentResponse[]> => {
    setLoading(true)
    setError(null)
    try {
      const url = status ? `${API_BASE}/agents?status=${status}` : `${API_BASE}/agents`
      const response = await fetchWithAuth(url, {
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail ?? `Failed to list agents: ${response.statusText}`)
      }

      const data: AgentResponse[] = await response.json()
      setAgents(data)
      return data
    } catch (err) {
      const errorMessage = handleError(err)
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [handleError])

  const getAgent = useCallback(async (agentId: string): Promise<AgentDetailResponse> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetchWithAuth(`${API_BASE}/agents/${encodeURIComponent(agentId)}`, {
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail ?? `Failed to get agent: ${response.statusText}`)
      }

      const data: AgentDetailResponse = await response.json()
      return data
    } catch (err) {
      const errorMessage = handleError(err)
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [handleError])

  const registerAgent = useCallback(
    async (data: AgentRegistrationRequest): Promise<AgentResponse> => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchWithAuth(`${API_BASE}/agents/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail ?? `Failed to register agent: ${response.statusText}`)
        }

        const result: AgentResponse = await response.json()
        await listAgents() // Refresh list
        return result
      } catch (err) {
        const errorMessage = handleError(err)
        setError(errorMessage)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [handleError, listAgents]
  )

  const updateAgent = useCallback(
    async (agentId: string, data: AgentUpdateRequest): Promise<AgentResponse> => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchWithAuth(`${API_BASE}/agents/${encodeURIComponent(agentId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail ?? `Failed to update agent: ${response.statusText}`)
        }

        const result: AgentResponse = await response.json()
        await listAgents() // Refresh list
        return result
      } catch (err) {
        const errorMessage = handleError(err)
        setError(errorMessage)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [handleError, listAgents]
  )

  const deleteAgent = useCallback(
    async (agentId: string): Promise<void> => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchWithAuth(`${API_BASE}/agents/${encodeURIComponent(agentId)}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail ?? `Failed to delete agent: ${response.statusText}`)
        }

        await listAgents() // Refresh list
      } catch (err) {
        const errorMessage = handleError(err)
        setError(errorMessage)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [handleError, listAgents]
  )

  const validateAgent = useCallback(
    async (agentId: string): Promise<Record<string, unknown>> => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchWithAuth(`${API_BASE}/agents/${encodeURIComponent(agentId)}/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail ?? `Failed to validate agent: ${response.statusText}`)
        }

        const result: Record<string, unknown> = await response.json()
        await listAgents() // Refresh list to get updated status
        return result
      } catch (err) {
        const errorMessage = handleError(err)
        setError(errorMessage)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [handleError, listAgents]
  )

  const healthCheckAgent = useCallback(
    async (agentId: string): Promise<boolean> => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchWithAuth(`${API_BASE}/agents/${encodeURIComponent(agentId)}/health-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail ?? `Failed to health check agent: ${response.statusText}`)
        }

        const result: boolean = await response.json()
        await listAgents() // Refresh list to get updated health status
        return result
      } catch (err) {
        const errorMessage = handleError(err)
        setError(errorMessage)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [handleError, listAgents]
  )

  const activateAgent = useCallback(
    async (agentId: string): Promise<AgentResponse> => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchWithAuth(`${API_BASE}/agents/${encodeURIComponent(agentId)}/activate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail ?? `Failed to activate agent: ${response.statusText}`)
        }

        const result: AgentResponse = await response.json()
        await listAgents() // Refresh list
        return result
      } catch (err) {
        const errorMessage = handleError(err)
        setError(errorMessage)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [handleError, listAgents]
  )

  const deactivateAgent = useCallback(
    async (agentId: string): Promise<AgentResponse> => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchWithAuth(`${API_BASE}/agents/${encodeURIComponent(agentId)}/deactivate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail ?? `Failed to deactivate agent: ${response.statusText}`)
        }

        const result: AgentResponse = await response.json()
        await listAgents() // Refresh list
        return result
      } catch (err) {
        const errorMessage = handleError(err)
        setError(errorMessage)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [handleError, listAgents]
  )

  const refreshAgents = useCallback(async () => {
    await listAgents()
  }, [listAgents])

  const testConnection = useCallback(
    async (agentId: string): Promise<{ connected: boolean }> => {
      const response = await fetchWithAuth(
        `${API_BASE}/agents/${agentId}/database-connection/test`,
        { method: 'POST' }
      )
      if (!response.ok) throw new Error(`Test connection failed: ${response.status}`)
      return response.json()
    },
    []
  )

  const getSchema = useCallback(
    async (agentId: string): Promise<Record<string, unknown>> => {
      const response = await fetchWithAuth(`${API_BASE}/agents/${agentId}/schema`)
      if (!response.ok) throw new Error(`Get schema failed: ${response.status}`)
      return response.json()
    },
    []
  )

  const introspectSchema = useCallback(
    async (agentId: string): Promise<Record<string, unknown>> => {
      const response = await fetchWithAuth(
        `${API_BASE}/agents/${agentId}/schema/introspect`,
        { method: 'POST' }
      )
      if (!response.ok) throw new Error(`Introspect schema failed: ${response.status}`)
      return response.json()
    },
    []
  )

  const enrichSchema = useCallback(
    async (agentId: string): Promise<Record<string, unknown>> => {
      const response = await fetchWithAuth(
        `${API_BASE}/agents/${encodeURIComponent(agentId)}/schema/enrich`,
        { method: 'POST' }
      )
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail ?? `Enrich schema failed: ${response.status}`)
      }
      return response.json()
    },
    []
  )

  const testConnectionAdhoc = useCallback(
    async (config: DatabaseConnectionConfig): Promise<{ connected: boolean; message: string }> => {
      const response = await fetchWithAuth(`${API_BASE}/agents/test-connection-adhoc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail ?? 'Test connection failed')
      }
      return response.json()
    },
    []
  )

  const checkBaseUrl = useCallback(
    async (baseUrl: string, excludeAgentId?: string): Promise<CheckBaseUrlResponse> => {
      const response = await fetchWithAuth(`${API_BASE}/agents/check-base-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ base_url: baseUrl, exclude_agent_id: excludeAgentId }),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail ?? 'Failed to check base URL')
      }
      return response.json()
    },
    []
  )

  const getDbConnection = useCallback(
    async (agentId: string): Promise<DatabaseConnectionInfo> => {
      const response = await fetchWithAuth(
        `${API_BASE}/agents/${encodeURIComponent(agentId)}/database-connection`
      )
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('No database connection configured')
        }
        throw new Error(`Failed to get database connection: ${response.status}`)
      }
      return response.json()
    },
    []
  )

  const fetchAgentInfo = useCallback(
    async (baseUrl: string): Promise<{ description?: string } | null> => {
      try {
        const url = `${baseUrl.replace(/\/+$/, '')}/agent/info`
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(5000),
        })
        if (!response.ok) return null
        return response.json()
      } catch {
        return null
      }
    },
    []
  )

  return {
    agents,
    loading,
    error,
    listAgents,
    getAgent,
    registerAgent,
    updateAgent,
    deleteAgent,
    validateAgent,
    healthCheckAgent,
    activateAgent,
    deactivateAgent,
    refreshAgents,
    testConnection,
    getSchema,
    introspectSchema,
    enrichSchema,
    testConnectionAdhoc,
    checkBaseUrl,
    getDbConnection,
    fetchAgentInfo,
  }
}
